import { api } from '@/shared/api/axios';
import { unwrapList, unwrapSingle } from '@/shared/api/unwrap';

const base = (depId) => `/deployments/${depId}`;

export const deploymentUsersApi = {
  list: (depId) => api.get(`${base(depId)}/users`).then(unwrapList),
  create: (depId, payload) => api.post(`${base(depId)}/users`, payload).then((r) => r.data),
  bulkCreate: (depId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`${base(depId)}/users/bulk`, fd).then((r) => r.data);
  },
  update: (depId, userId, payload) => api.patch(`${base(depId)}/users/${userId}`, payload).then((r) => r.data),
  delete: (depId, userId) => api.delete(`${base(depId)}/users/${userId}`).then((r) => r.data),
};

export const parametersApi = {
  list: (depId) => api.get(`${base(depId)}/parameters`).then(unwrapList),
  live: (depId) => api.get(`${base(depId)}/parameters/live`).then(unwrapList),
  stats: (depId, from, to) => api.get(`${base(depId)}/parameters/stats`, { params: { from, to } }).then(unwrapList),
  create: (depId, payload) => api.post(`${base(depId)}/parameters`, payload).then((r) => r.data),
  bulkCreate: (depId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`${base(depId)}/parameters/bulk`, fd).then((r) => r.data);
  },
  update: (depId, paramId, payload) => api.patch(`${base(depId)}/parameters/${paramId}`, payload).then((r) => r.data),
  delete: (depId, paramId) => api.delete(`${base(depId)}/parameters/${paramId}`).then((r) => r.data),
  charts: (depId, paramId, from, to, chartType) =>
    api.get(`${base(depId)}/parameters/${paramId}/charts`, { params: { from, to, ...(chartType ? { chart_type: chartType } : {}) } }).then(unwrapSingle),
};

export const layersApi = {
  list: (depId) => api.get(`${base(depId)}/layers`).then(unwrapList),
  create: (depId, payload) => api.post(`${base(depId)}/layers`, payload).then((r) => r.data),
  update: (depId, layerId, payload) => api.patch(`${base(depId)}/layers/${layerId}`, payload).then((r) => r.data),
  delete: (depId, layerId) => api.delete(`${base(depId)}/layers/${layerId}`).then((r) => r.data),
  bulkCreateMapping: (depId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`${base(depId)}/layers/mapping/bulk`, fd).then((r) => r.data);
  },
};

export const overviewConfigApi = {
  // Returns the config object, or `null` when the deployment has no overview
  // config yet. `null` is a meaningful state: DeploymentOverviewPage uses
  // `!config` to distinguish "not configured" (show setup prompt) from
  // "configured but empty". Do NOT substitute a typed empty object here — it
  // would collapse that distinction. A malformed (non-object) response also
  // normalizes to `null` so the page shows its empty/setup state rather than
  // crashing on field access.
  get: (depId) =>
    api.get(`${base(depId)}/overview-config`).then((r) => {
      const config = r.data?.data ?? null;
      return config && typeof config === 'object' && !Array.isArray(config) ? config : null;
    }),
  put: (depId, payload) => api.put(`${base(depId)}/overview-config`, payload).then(unwrapSingle),
};

export const svgTemplatesApi = {
  // Global catalog of available HMI/diagram templates. Each item carries an
  // svgUrl the FE fetches + inlines, and a sections list of bindable hotspots.
  list: () => api.get('/svg-templates').then(unwrapList),

  // Create — multipart (name + .svg file). Sections are auto-derived BE-side.
  create: ({ name, file }) => {
    const fd = new FormData();
    fd.append('name', name);
    fd.append('file', file);
    return api.post('/svg-templates', fd).then((r) => r.data?.data ?? r.data);
  },

  // Update — multipart; send `name`, `file`, or both. Replacing the file
  // re-derives sections and must keep every currently-bound region.
  update: (id, { name, file }) => {
    const fd = new FormData();
    if (name != null) fd.append('name', name);
    if (file != null) fd.append('file', file);
    return api.patch(`/svg-templates/${id}`, fd).then((r) => r.data?.data ?? r.data);
  },

  // Delete — 409 SVG_TEMPLATE_IN_USE if any deployment still binds it.
  remove: (id) => api.delete(`/svg-templates/${id}`).then((r) => r.data),
};
